import { fmtNum, normalizeSpecs, parseNum, type ItemSpecs, t } from '@procumi/shared';
import { useState } from 'react';

/**
 * Chestionarul pentru cofraje. „500 mp de cofraje” nu e o cerere: lipsesc
 * elementul, înălțimea și durata — de aceea, pe aceeași lucrare, Alsina a
 * ofertat pereți, iar Doka planșeu (jurnalul, 28 septembrie). Răspunsurile fac
 * o poziție completă, pe care furnizorii o înțeleg la fel.
 *
 * Mai multe înălțimi („3; 4; 6”): cantitățile pe înălțimi diferite nu se adună.
 * Fie o singură poziție cu toate înălțimile (la planșeu, aceleași materiale,
 * diferă doar popii), fie câte o poziție pe înălțime, cu cantitatea ei.
 */

const ELEMENTS = [
  { key: 'pereti', label: t('Pereți'), size: t('Înălțime turnare (m)'), desc: t('cofraj vertical pereți') },
  { key: 'stalpi', label: t('Stâlpi'), size: t('Înălțime turnare (m)'), desc: t('cofraj stâlpi') },
  { key: 'fundatii', label: t('Fundații'), size: t('Înălțime turnare (m)'), desc: t('cofraj fundații') },
  { key: 'grinzi', label: t('Grinzi'), size: t('Înălțime grindă (cm)'), desc: t('cofraj grinzi') },
  { key: 'planseu', label: t('Planșeu'), size: t('Grosime placă (cm)'), desc: t('cofraj planșeu') },
] as const;

export interface CofrajItem {
  desc: string;
  qty: number;
  unit: string;
  rentDays: number | null;
  specs: ItemSpecs | null;
}

/**
 * Înălțimile scrise de om: „3; 4; 6”, „3 / 4 / 6”, „3, 4 și 6”. Virgula urmată
 * de cifră e zecimală („3,5”); urmată de spațiu, desparte.
 */
function heightsOf(v: string): number[] | null {
  const parts = v
    // fără \b: în JavaScript, „ș” nu e literă pentru \b, deci „3 și 4” nu s-ar despărți
    .split(/\s*(?:;|\/|,(?=\s))\s*|\s+(?:și|si)\s+/i)
    .map((x) => x.trim())
    .filter(Boolean);
  if (!parts.length) return null;
  const nums = parts.map((x) => parseNum(x));
  if (nums.some((n) => n == null || n <= 0)) return null;
  return [...new Set(nums as number[])].sort((a, b) => a - b);
}

const hText = (hs: number[]) => hs.map(fmtNum).join(' / ');

export function CofrajHelper({
  onAdd,
  submitLabel = t('Adaugă în poziții'),
}: {
  onAdd: (items: CofrajItem[]) => void;
  /** pe necesar, liniile se cheamă „materiale” */
  submitLabel?: string;
}) {
  const [element, setElement] = useState<(typeof ELEMENTS)[number]['key']>('pereti');
  const [area, setArea] = useState('');
  const [size, setSize] = useState('');
  const [height, setHeight] = useState('');
  const [rent, setRent] = useState(true);
  const [days, setDays] = useState('90');
  /** la mai multe înălțimi: o poziție (aceleași materiale) sau câte una pe înălțime */
  const [split, setSplit] = useState(false);
  const [perHeight, setPerHeight] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  const el = ELEMENTS.find((e) => e.key === element)!;
  const slab = element === 'planseu';
  // Unde se scrie înălțimea: la planșeu, sprijinirea; la pereți, stâlpi, fundații, turnarea.
  const sizeIsHeight = el.size.includes('(m)');
  const heightField = slab ? height : sizeIsHeight ? size : '';
  const hs = heightField.trim() ? heightsOf(heightField) : null;
  const many = (hs?.length ?? 0) > 1;

  const add = () => {
    const d = parseNum(days);
    if (rent && (d == null || d < 1 || !Number.isInteger(d))) {
      return setError(t('Scrie durata chiriei în zile întregi.'));
    }
    if ((slab || sizeIsHeight) && !hs) {
      return setError(
        slab
          ? t('Scrie înălțimea de sprijinire, în m. Pentru mai multe: 3; 4; 6.')
          : t('Completează: {0}. Pentru mai multe: 3; 4; 6.', el.size.toLowerCase()),
      );
    }
    // grosimea plăcii sau înălțimea grinzii, în cm
    const cm = sizeIsHeight ? null : parseNum(size);
    if (!sizeIsHeight && (cm == null || cm <= 0)) {
      return setError(t('Completează: {0}.', el.size.toLowerCase()));
    }

    const name = el.desc.charAt(0).toUpperCase() + el.desc.slice(1);
    const cmPart = cm != null ? `${el.size.replace(/ \(.*\)$/, '').toLowerCase()} ${fmtNum(cm)} cm` : '';
    const hPart = (list: number[]) =>
      slab
        ? t('sprijinire la {0} m', hText(list))
        : sizeIsHeight
          ? t('înălțime turnare {0} m', hText(list))
          : '';
    const make = (list: number[], qty: number): CofrajItem => ({
      desc: [name, cmPart, hPart(list)].filter(Boolean).join(', '),
      qty,
      unit: t('mp'),
      rentDays: rent ? d! : null,
      specs: normalizeSpecs({ heights: list, thickness: cm }),
    });

    if (many && split) {
      const items: CofrajItem[] = [];
      for (const h of hs!) {
        const q = parseNum(perHeight[String(h)] ?? '');
        if (q == null || q <= 0) return setError(t('Scrie suprafața pentru înălțimea de {0} m.', fmtNum(h)));
        items.push(make([h], q));
      }
      onAdd(items);
    } else {
      const qty = parseNum(area);
      if (qty == null || qty <= 0) return setError(t('Scrie suprafața de cofrat, în mp.'));
      onAdd([make(hs ?? [], qty)]);
    }
    setArea('');
    setSize('');
    setHeight('');
    setPerHeight({});
    setError('');
  };

  return (
    <div className="helper">
      <div className="helper-h">
        <h3>{t('Poziție de cofraj')}</h3>
        <span className="hint" style={{ margin: 0 }}>
          {t('Ca furnizorii să oferteze același lucru și ofertele să se poată compara.')}
        </span>
      </div>
      <div className="row">
        <label className="f">
          {t('Element')}
          <select
            className="inp"
            value={element}
            onChange={(e) => setElement(e.target.value as typeof element)}
          >
            {ELEMENTS.map((e) => (
              <option key={e.key} value={e.key}>
                {e.label}
              </option>
            ))}
          </select>
        </label>
        {many && split ? null : (
          <label className="f">
            {t('Suprafață (mp)')}
            <input
              className="inp"
              inputMode="decimal"
              value={area}
              onChange={(e) => setArea(e.target.value)}
            />
          </label>
        )}
        <label className="f">
          {el.size}
          <input
            className="inp"
            inputMode="decimal"
            placeholder={sizeIsHeight ? t('3 sau 3; 4; 6') : undefined}
            value={size}
            onChange={(e) => setSize(e.target.value)}
          />
        </label>
        {slab ? (
          <label className="f">
            {t('Înălțime sprijinire (m)')}
            <input
              className="inp"
              inputMode="decimal"
              placeholder={t('3 sau 3; 4; 6')}
              value={height}
              onChange={(e) => setHeight(e.target.value)}
            />
          </label>
        ) : null}
        <label className="f">
          {t('Regim')}
          <select
            className="inp"
            value={rent ? 'rent' : 'buy'}
            onChange={(e) => setRent(e.target.value === 'rent')}
          >
            <option value="rent">{t('Închiriere')}</option>
            <option value="buy">{t('Cumpărare')}</option>
          </select>
        </label>
        {rent ? (
          <label className="f">
            {t('Durată estimată (zile)')}
            <input
              className="inp"
              inputMode="numeric"
              value={days}
              onChange={(e) => setDays(e.target.value)}
            />
          </label>
        ) : null}
      </div>
      {many ? (
        <div className="helper-heights">
          <span className="hint" style={{ margin: 0 }}>
            {t(
              'Ai scris {0} înălțimi: {1} m. Cantitățile de la înălțimi diferite nu se adună.',
              hs!.length,
              hText(hs!),
            )}
          </span>
          <div className="checks" role="radiogroup" aria-label={t('Cum se cer înălțimile')}>
            <label>
              <input type="radio" checked={!split} onChange={() => setSplit(false)} />
              <span>
                {t('O singură poziție')}
                <span className="muted">
                  {slab
                    ? t('· aceleași materiale, diferă doar înălțimea popilor')
                    : t('· aceleași materiale, diferă doar înălțimea')}
                </span>
              </span>
            </label>
            <label>
              <input type="radio" checked={split} onChange={() => setSplit(true)} />
              <span>
                {t('Câte o poziție pe înălțime')}{' '}
                <span className="muted">{t('· fiecare cu suprafața ei')}</span>
              </span>
            </label>
          </div>
          {split ? (
            <div className="row">
              {hs!.map((h) => (
                <label className="f" key={h}>
                  {t('Suprafață la {0} m (mp)', fmtNum(h))}
                  <input
                    className="inp"
                    inputMode="decimal"
                    value={perHeight[String(h)] ?? ''}
                    onChange={(e) => setPerHeight((p) => ({ ...p, [String(h)]: e.target.value }))}
                  />
                </label>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {error ? <p className="hint warn-text">{error}</p> : null}
      {/* type="button": pe necesar, chestionarul stă într-un formular */}
      <button type="button" className="btn small" style={{ marginTop: 12 }} onClick={add}>
        {many && split ? t('Adaugă {0} poziții', hs!.length) : submitLabel}
      </button>
    </div>
  );
}
